/**
 * The language the app starts in.
 *
 * The URL wins. A locale-prefixed pathname (`/en/about`) is an explicit
 * statement of the language the visitor asked for, and for a first-time
 * visitor it is the ONLY signal there is — no localStorage, no switcher
 * history. `?locale=` comes next (legacy links that still carry it),
 * then the stored preference, then the default.
 *
 * The admin's real-site-preview iframe is also served by rule 1: the
 * preview URL is built in its final, locale-prefixed form
 * (`buildPreviewUrl` in `server/lib/preview-token.ts`) precisely so that
 * no canonicalisation redirect rewrites it and drops the preview token.
 *
 * WHY THIS IS NOT A DETECTOR
 * --------------------------
 * i18next skips the detector chain entirely when `lng` is passed to `init`,
 * so a detector alone cannot decide the initial language. This file used to
 * set `lng` from localStorage AND register a `localePath` detector, with a
 * comment claiming the detector would win for prefixed URLs. It never did:
 * `lng` was set, the detector never ran in the browser, and every
 * locale-prefixed URL rendered in the stored/default language.
 *
 * The server side was unaffected — `entry-server.tsx` calls `changeLanguage`
 * explicitly — which is why the prerendered HTML was correct while the
 * browser threw it away and re-rendered in zh-Hant (see `main.tsx`: it uses
 * `createRoot`, not `hydrateRoot`, so the prerendered markup is replaced
 * rather than adopted).
 *
 * One resolver, one priority order. The old split — a `lng` that decided
 * things and a detector that documented an intent it could not carry — is
 * what made the bug invisible to the SEO audit, which only reads the served
 * HTML.
 *
 * `resolveInitialLanguage` is pure so it can be asserted directly by
 * `scripts/test-locale-path.mjs`. A marker-grep lint cannot catch this
 * class of bug: the old `lint:locale-path-detector` checked that the
 * detector's call site was present and stayed green while the detector was
 * dead code that never executed.
 */

import {
  parseLocaleFromPath,
  PREFIXED_LOCALES,
  DEFAULT_LOCALE,
  type Locale,
} from '../utils/localePath'

/** Where the visitor's explicit language choice is remembered. */
export const STORAGE_KEY = 'yada-lang'

/** Whether a string is one of the locales the site ships. */
export function isLocale(value: string): value is Locale {
  return (PREFIXED_LOCALES as readonly string[]).includes(value)
}

/**
 * Resolve the initial language from the three inputs.
 *
 * Pure: no browser globals, so it can be asserted directly.
 */
export function resolveInitialLanguage(input: {
  pathname: string
  /** `window.location.search`, including the leading `?`. */
  search?: string
  /** The stored preference, if any. */
  stored?: string | null
}): Locale {
  const { pathname, search = '', stored = null } = input

  // 1. The URL prefix. This is the case that matters: it is what a
  //    first-time visitor arriving from a search engine sees.
  const hasLocalePrefix = PREFIXED_LOCALES.some(
    (l) => pathname === `/${l}` || pathname.startsWith(`/${l}/`),
  )
  if (hasLocalePrefix) return parseLocaleFromPath(pathname)

  // 2. `?locale=` — a legacy link that still carries it. The admin's
  //    real-site-preview iframe no longer needs this: its URL is built in
  //    the final, locale-prefixed form, so rule 1 serves it.
  const queried = new URLSearchParams(search).get('locale')
  if (queried && isLocale(queried)) return queried

  // 3. The stored preference — the fallback for the canonical (bare) path.
  if (stored && isLocale(stored)) return stored

  // 4. The default.
  return DEFAULT_LOCALE
}

/**
 * The browser-side entry point: read the current URL and storage, resolve.
 *
 * Guarded for the server, where `window` and `localStorage` do not exist.
 * The prerender path installs a localStorage stub and then forces the
 * language explicitly (see `entry-server.tsx`), so the SSR result is
 * unchanged by anything here.
 */
export function initialLanguage(): Locale {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  const search = typeof window !== 'undefined' ? window.location.search : ''

  let stored: string | null = null
  try {
    stored = localStorage.getItem(STORAGE_KEY)
  } catch {
    // Storage unavailable (private mode, disabled cookies) — fall through.
    stored = null
  }

  return resolveInitialLanguage({ pathname, search, stored })
}
